import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Sparkles, Square } from 'lucide-react';
import { Button, IconButton, Spinner } from '../../../components/ui';
import { userStorage } from '../../../utils/auth';
import { AiNotice, canResearch, currentUserRole, isAdminRole, StreamProgress } from '../components/BriefPanel';
import { ConfidenceBadge, EvidenceList } from '../components/Confidence';
import { acceptLead, dismissLead, listLeads } from '../intelligenceApi';
import type { InfluenceLead } from '../types';
import type { StreamState } from '../useIntelligenceStream';
import { InfluenceArea, InfluenceEntity } from './influenceModel';
import { AnimatedBar, areaColor, DeltaPill, relativeTime } from './ui';

export interface AssessStream extends StreamState<{ lead: InfluenceLead }> {
  subjectId: string | null;
  start: (entity: InfluenceEntity, focus?: string) => void;
  stop: () => void;
}

interface AssessPanelProps {
  entity: InfluenceEntity;
  areas: InfluenceArea[];
  stream: AssessStream;
  /** Proposed scores of the lead under review (for the radar), null when none */
  onPreview: (scores: Record<string, number> | null) => void;
  /** Called after scores were accepted so the page can reload */
  onAccepted: (lead: InfluenceLead) => void;
}

export function canReviewLead(lead: { createdBy?: string | null }, role = currentUserRole(), userId = userStorage.get()?.id): boolean {
  if (isAdminRole(role)) return true;
  return role === 'top_owner' && !!lead.createdBy && lead.createdBy === userId;
}

export const AssessPanel: React.FC<AssessPanelProps> = ({ entity, areas, stream, onPreview, onAccepted }) => {
  const [leads, setLeads] = useState<InfluenceLead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ id: string; action: 'accept' | 'dismiss' } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acceptedAt, setAcceptedAt] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [focus, setFocus] = useState('');
  const [steer, setSteer] = useState(false);
  const allowed = canResearch();

  const mine = stream.subjectId === entity.id;
  const running = mine && stream.status === 'running';

  // Pending influence leads for this entity
  useEffect(() => {
    let cancelled = false;
    setLeads([]);
    setOpenId(null);
    setActionError(null);
    setAcceptedAt(null);
    setDismissed(false);
    setLoadingLeads(true);
    listLeads({ kind: 'influence', status: 'pending', subjectId: entity.id })
      .then((list) => {
        if (cancelled) return;
        const pending = (Array.isArray(list) ? list : []).filter((l): l is InfluenceLead => l.kind === 'influence' && l.status === 'pending');
        setLeads(pending);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoadingLeads(false));
    return () => {
      cancelled = true;
    };
  }, [entity.id]);

  // A fresh assessment opens for review
  const fresh = mine ? stream.result?.lead : null;
  useEffect(() => {
    if (!fresh || fresh.subjectId !== entity.id) return;
    setLeads((list) => [fresh, ...list.filter((l) => l.id !== fresh.id)]);
    setOpenId(fresh.id);
    setAcceptedAt(null);
    setDismissed(false);
  }, [fresh, entity.id]);

  const open = leads.find((l) => l.id === openId) || null;
  useEffect(() => {
    if (!open) {
      onPreview(null);
      return;
    }
    const values: Record<string, number> = {};
    entity.scores.forEach((s) => (values[s.categoryId] = s.score));
    open.scores.forEach((s) => (values[s.categoryId] = s.score));
    onPreview(values);
  }, [open, entity.scores, onPreview]);
  useEffect(() => () => onPreview(null), [onPreview]);

  const act = useCallback(
    async (lead: InfluenceLead, action: 'accept' | 'dismiss') => {
      setBusy({ id: lead.id, action });
      setActionError(null);
      try {
        if (action === 'accept') await acceptLead(lead.id);
        else await dismissLead(lead.id);
        setLeads((list) => list.filter((l) => l.id !== lead.id));
        setOpenId((id) => (id === lead.id ? null : id));
        setDismissed(action === 'dismiss');
        if (action === 'accept') {
          setAcceptedAt(Date.now());
          onAccepted(lead);
        } else {
          setAcceptedAt(null);
        }
      } catch (err: any) {
        const message = err?.response?.data?.message;
        setActionError(Array.isArray(message) ? message.join(', ') : message || err?.message || 'Could not update the lead');
      } finally {
        setBusy(null);
      }
    },
    [onAccepted],
  );

  // A stream can end as 'done' while carrying an error (the shared hook may overwrite 'error'), so check both
  const failed = mine && (stream.status === 'error' || stream.status === 'unavailable' || (stream.status === 'done' && !!stream.error));

  return (
    <div className="space-y-3">
      {allowed && !running && (
        <div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => stream.start(entity, focus.trim() || undefined)} className="flex-1 sm:flex-none">
              <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
              Assess with AI
            </Button>
            <IconButton label={steer ? 'Hide focus' : 'Add a focus'} size="sm" onClick={() => setSteer((v) => !v)} aria-expanded={steer}>
              <ChevronDown className={`h-4 w-4 transition-transform ${steer ? 'rotate-180' : ''}`} strokeWidth={1.75} />
            </IconButton>
          </div>
          {steer && (
            <input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="Focus, e.g. mining permits"
              aria-label="Assessment focus"
              className="mt-2 block h-9 w-full rounded-xl border-0 bg-neutral-100 px-3 text-[16px] text-ink placeholder:text-neutral-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:text-[13px] infl-rise"
            />
          )}
        </div>
      )}
      {!allowed && !leads.length && <p className="text-[12px] text-neutral-400">AI assessment is available to admins and owners</p>}

      {running && (
        <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[12px] text-neutral-500">Researching {entity.label}</span>
            <IconButton label="Stop" size="xs" onClick={stream.stop}>
              <Square className="h-3 w-3 fill-current" strokeWidth={1.75} />
            </IconButton>
          </div>
          <StreamProgress lines={stream.progress} running max={5} />
        </div>
      )}
      {failed && (
        <AiNotice
          status={stream.status === 'unavailable' ? 'unavailable' : 'error'}
          error={/failed to fetch|networkerror|load failed/i.test(stream.error || '') ? 'Couldn’t reach the server' : stream.error}
          onRetry={() => stream.start(entity, focus.trim() || undefined)}
        />
      )}
      {mine && stream.status === 'done' && !stream.result?.lead && !stream.error && (
        <p className="text-[12px] text-neutral-400">No assessment returned</p>
      )}

      {acceptedAt && (
        <p className="flex items-center gap-1.5 text-[12px] text-emerald-700 infl-rise" role="status">
          <Check className="h-3.5 w-3.5" strokeWidth={2} />
          Scores updated
        </p>
      )}
      {dismissed && !acceptedAt && (
        <p className="text-[12px] text-neutral-400 infl-rise" role="status">
          Assessment dismissed — scores unchanged
        </p>
      )}
      {actionError && <p className="rounded-xl bg-primary-50 px-3 py-2 text-[12.5px] text-primary-700">{actionError}</p>}

      {loadingLeads && !leads.length && (
        <p className="flex items-center gap-2 text-[12px] text-neutral-400">
          <Spinner className="h-3 w-3" /> Checking pending assessments
        </p>
      )}

      {leads.length > 0 && (
        <ul className="space-y-2">
          {leads.map((lead) =>
            lead.id === openId ? (
              <li key={lead.id}>
                <LeadReview
                  lead={lead}
                  entity={entity}
                  areas={areas}
                  busy={busy?.id === lead.id ? busy.action : null}
                  canReview={canReviewLead(lead)}
                  onAccept={() => act(lead, 'accept')}
                  onDismiss={() => act(lead, 'dismiss')}
                  onCollapse={() => setOpenId(null)}
                />
              </li>
            ) : (
              <li key={lead.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(lead.id)}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-ink">Pending assessment</span>
                    <span className="block truncate text-[11.5px] text-neutral-400">
                      {relativeTime(lead.createdAt) || 'recently'} · {lead.scores.length} area{lead.scores.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  <ConfidenceBadge value={lead.confidence} showValue={false} />
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
};

const LeadReview: React.FC<{
  lead: InfluenceLead;
  entity: InfluenceEntity;
  areas: InfluenceArea[];
  busy: 'accept' | 'dismiss' | null;
  canReview: boolean;
  onAccept: () => void;
  onDismiss: () => void;
  onCollapse: () => void;
}> = ({ lead, entity, areas, busy, canReview, onAccept, onDismiss, onCollapse }) => {
  const [sources, setSources] = useState(false);
  const areaById = useMemo(() => new Map(areas.map((a) => [a.id, a] as [string, InfluenceArea])), [areas]);
  const rows = lead.scores
    .map((s) => {
      const current = entity.scoreByArea[s.categoryId]?.score ?? s.previousScore ?? null;
      return { ...s, current, delta: current == null ? null : Math.round(s.score - current), area: areaById.get(s.categoryId) };
    })
    .sort((a, b) => b.score - a.score);
  const meta = [relativeTime(lead.createdAt), lead.model].filter(Boolean).join(' · ');

  return (
    <div className="overflow-hidden rounded-2xl ring-1 ring-inset ring-neutral-200 infl-rise">
      <div className="flex items-start gap-2 px-3 pb-2 pt-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[13px] text-ink">
            <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
            Proposed scores
          </p>
          {meta && <p className="mt-0.5 truncate text-[11.5px] text-neutral-400">{meta}</p>}
        </div>
        <ConfidenceBadge value={lead.confidence} />
        <IconButton label="Collapse" size="xs" onClick={onCollapse} className="-mr-1">
          <ChevronDown className="h-3.5 w-3.5 rotate-180" strokeWidth={1.75} />
        </IconButton>
      </div>
      {lead.summary && <p className="px-3 pb-2 text-[12.5px] leading-relaxed text-neutral-600">{lead.summary}</p>}

      <ul className="divide-y divide-neutral-100 border-t border-neutral-100">
        {rows.map((row) => (
          <li key={row.categoryId} className="px-3 py-2.5">
            <div className="flex items-center gap-2 text-[13px]">
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(row.area?.color) }} />
              <span className="min-w-0 flex-1 truncate text-ink">{row.area?.label || row.categoryLabel}</span>
              <span className="tabular-nums text-neutral-400">{row.current ?? '—'}</span>
              <span className="text-neutral-300">→</span>
              <span className="w-6 text-right tabular-nums text-ink">{row.score}</span>
              <DeltaPill delta={row.delta} />
            </div>
            <AnimatedBar value={row.current ?? 0} ghost={row.score} color={row.area?.color} className="mt-1.5" />
            <div className="mt-1.5 flex items-start justify-between gap-2">
              <p className="min-w-0 text-[12px] leading-snug text-neutral-500">{row.rationale}</p>
              <ConfidenceBadge value={row.confidence} showValue={false} className="flex-shrink-0 scale-90" />
            </div>
          </li>
        ))}
      </ul>

      <div className="border-t border-neutral-100 px-3 py-2">
        <button
          type="button"
          onClick={() => setSources((v) => !v)}
          aria-expanded={sources}
          className="flex w-full items-center justify-between text-[12px] text-neutral-500 hover:text-ink"
        >
          Sources <span className="flex items-center gap-1 tabular-nums">{lead.evidence.length}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${sources ? 'rotate-180' : ''}`} strokeWidth={1.75} /></span>
        </button>
        {sources && <EvidenceList evidence={lead.evidence} className="mt-2 infl-rise" />}
      </div>

      {canReview ? (
        <div className="flex gap-2 border-t border-neutral-100 px-3 py-2.5">
          <Button size="sm" variant="primary" className="flex-1" loading={busy === 'accept'} disabled={!!busy} onClick={onAccept}>
            Accept
          </Button>
          <Button size="sm" variant="secondary" className="flex-1" loading={busy === 'dismiss'} disabled={!!busy} onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      ) : (
        <p className="border-t border-neutral-100 px-3 py-2 text-[12px] text-neutral-400">Awaiting review by an admin</p>
      )}
    </div>
  );
};
